import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { useStore } from '../context/StoreContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Calendar } from '../components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Checkbox } from '../components/ui/checkbox';
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { 
  CalendarIcon,
  Plus,
  Users,
  Loader2,
  TrendingUp,
  Landmark,
  ArrowUpRight,
  ArrowDownRight,
  Pencil,
  Wallet,
  ChevronRight,
  X,
  Banknote,
  Shield,
  Clock,
  CheckCircle2,
  FileText,
  RotateCcw
} from 'lucide-react';
import { Badge } from '../components/ui/badge';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const CreditorsPage = () => {
  const { isAdmin, isManager, user, token } = useAuth();
  const { stores, selectedStore } = useStore();
  const [selectedStoreId, setSelectedStoreId] = useState('');
  const [activeTab, setActiveTab] = useState('overview');
  const [summary, setSummary] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [creditors, setCreditors] = useState([]);
  const [schemes, setSchemes] = useState([]);
  const [loading, setLoading] = useState(false);
  
  // Check if user is staff (not admin or manager)
  const isStaff = user?.user_type === 'staff';
  
  // Dialog states
  const [transactionDialogOpen, setTransactionDialogOpen] = useState(false);
  const [creditorDialogOpen, setCreditorDialogOpen] = useState(false);
  const [schemeDialogOpen, setSchemeDialogOpen] = useState(false);
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);
  const [selectedEntity, setSelectedEntity] = useState(null);
  const [entityTransactions, setEntityTransactions] = useState([]);
  const [saving, setSaving] = useState(false);
  const [editingTransaction, setEditingTransaction] = useState(null);
  
  // Unreconcile dialog state
  const [unreconcileDialog, setUnreconcileDialog] = useState({ open: false, transaction: null });
  const [unreconciling, setUnreconciling] = useState(false);
  
  // Manual reconcile dialog state (TEMPORARY - for legacy transactions)
  const [manualReconcileDialog, setManualReconcileDialog] = useState({ open: false, transaction: null });
  const [manualReconciling, setManualReconciling] = useState(false);
  const [manualReconcileNote, setManualReconcileNote] = useState('');
  
  // Transaction form
  const [transactionForm, setTransactionForm] = useState({
    date: new Date(),
    transaction_type: '',
    entity_id: '',
    entity_name: '',
    direction: 'out',
    source: 'cash_holding',
    description: '',
    amount: ''
  });
  
  // Creditor/Scheme form
  const [entityForm, setEntityForm] = useState({
    name: '',
    description: '',
    contact: '',
    email: '',
    opening_balance: ''
  });
  
  // Edit mode states
  const [editingCreditor, setEditingCreditor] = useState(null);
  const [editingScheme, setEditingScheme] = useState(null);

  // Cash and Safe balances
  const [balances, setBalances] = useState({ cash: null, safe: null });
  
  // Selected entity outstanding balance
  const [selectedEntityBalance, setSelectedEntityBalance] = useState(null);

  // Creditor requests for the detail dialog
  const [creditorRequests, setCreditorRequests] = useState([]);
  const [detailDialogTab, setDetailDialogTab] = useState('transactions');
  const [loadingRequests, setLoadingRequests] = useState(false);

  useEffect(() => {
    if (stores.length > 0) {
      const storeId = selectedStore?.id || stores[0].id;
      setSelectedStoreId(storeId);
    }
  }, [stores, selectedStore]);

  useEffect(() => {
    if (selectedStoreId) {
      fetchAllData();
    }
  }, [selectedStoreId]);

  const fetchAllData = async () => {
    setLoading(true);
    try {
      const [summaryRes, transactionsRes, creditorsRes, schemesRes, cashRes, safeRes] = await Promise.all([
        axios.get(`${API}/creditors/summary`, { params: { store_id: selectedStoreId } }),
        axios.get(`${API}/creditors/transactions`, { params: { store_id: selectedStoreId } }),
        axios.get(`${API}/creditors/creditors`, { params: { store_id: selectedStoreId } }),
        axios.get(`${API}/creditors/schemes`, { params: { store_id: selectedStoreId } }),
        axios.get(`${API}/money-movements/cash-balance/${selectedStoreId}`),
        axios.get(`${API}/money-movements/safe-balance/${selectedStoreId}`)
      ]);
      setSummary(summaryRes.data);
      setTransactions(transactionsRes.data);
      setCreditors(creditorsRes.data);
      setSchemes(schemesRes.data);
      setBalances({ cash: cashRes.data, safe: safeRes.data });
    } catch (error) {
      console.error('Failed to fetch data:', error);
      toast.error('Failed to load creditors data');
    } finally {
      setLoading(false);
    }
  };

  const openTransactionDialog = (transaction = null) => {
    if (transaction) {
      setEditingTransaction(transaction);
      setTransactionForm({
        date: new Date(transaction.date),
        transaction_type: transaction.transaction_type,
        entity_id: transaction.entity_id,
        entity_name: transaction.entity_name,
        direction: transaction.direction,
        source: transaction.source || 'cash_holding',
        description: transaction.description || '',
        amount: transaction.amount.toString()
      });
    } else {
      setEditingTransaction(null);
      setTransactionForm({
        date: new Date(),
        transaction_type: isStaff ? 'lending' : '', // Staff defaults to lending
        entity_id: '',
        entity_name: '',
        direction: 'out',
        source: 'cash_holding',
        description: '',
        amount: ''
      });
    }
    setTransactionDialogOpen(true);
  };

  const openCreditorDialog = () => {
    openNewCreditorDialog();
  };

  const openSchemeDialog = () => {
    openNewSchemeDialog();
  };

  const handleEntitySelect = (entityId, type) => {
    const list = type === 'lending' ? creditors : schemes;
    const entity = list.find(e => e.id === entityId);
    setTransactionForm(prev => ({
      ...prev,
      entity_id: entityId,
      entity_name: entity?.name || ''
    }));
    
    // Find the outstanding balance from summary data
    if (summary && entityId) {
      if (type === 'lending') {
        const creditor = summary.creditor_details?.find(c => c.id === entityId);
        setSelectedEntityBalance(creditor ? {
          name: creditor.name,
          outstanding: creditor.outstanding,
          total_lent: creditor.total_lent,
          total_returned: creditor.total_repaid,
          type: 'lending'
        } : null);
      } else {
        const scheme = summary.investment_details?.find(s => s.id === entityId);
        setSelectedEntityBalance(scheme ? {
          name: scheme.name,
          outstanding: scheme.outstanding,
          total_invested: scheme.total_invested,
          total_returned: scheme.total_returned,
          type: 'investment'
        } : null);
      }
    } else {
      setSelectedEntityBalance(null);
    }
  };

  const handleTransactionSubmit = async () => {
    if (!transactionForm.transaction_type) {
      toast.error('Please select a transaction type');
      return;
    }
    if (!transactionForm.entity_id) {
      toast.error('Please select a creditor or scheme');
      return;
    }
    if (!transactionForm.amount || parseFloat(transactionForm.amount) <= 0) {
      toast.error('Please enter a valid amount');
      return;
    }
    if (!transactionForm.description.trim()) {
      toast.error('Please enter a description');
      return;
    }

    setSaving(true);
    try {
      const payload = {
        ...transactionForm,
        store_id: selectedStoreId,
        date: format(transactionForm.date, 'yyyy-MM-dd'),
        amount: parseFloat(transactionForm.amount)
      };

      if (editingTransaction) {
        await axios.put(`${API}/creditors/transactions/${editingTransaction.id}`, payload);
        toast.success('Transaction updated!');
      } else {
        await axios.post(`${API}/creditors/transactions`, payload);
        toast.success('Transaction recorded!');
      }
      
      setTransactionDialogOpen(false);
      fetchAllData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save transaction');
    } finally {
      setSaving(false);
    }
  };

  const handleCreditorSubmit = async () => {
    if (!entityForm.name.trim()) {
      toast.error('Please enter a name');
      return;
    }

    setSaving(true);
    try {
      if (editingCreditor) {
        await axios.put(`${API}/creditors/creditors/${editingCreditor.id}`, {
          name: entityForm.name,
          description: entityForm.description,
          contact: entityForm.contact,
          email: entityForm.email,
          is_active: entityForm.is_active,
          opening_balance: parseFloat(entityForm.opening_balance) || 0
        });
        toast.success('Creditor updated!');
      } else {
        await axios.post(`${API}/creditors/creditors`, {
          ...entityForm,
          store_id: selectedStoreId,
          opening_balance: parseFloat(entityForm.opening_balance) || 0
        });
        toast.success('Creditor added!');
      }
      setCreditorDialogOpen(false);
      setEditingCreditor(null);
      setEntityForm({ name: '', description: '', contact: '', email: '', opening_balance: '', is_active: true });
      fetchAllData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save creditor');
    } finally {
      setSaving(false);
    }
  };

  const handleSchemeSubmit = async () => {
    if (!entityForm.name.trim()) {
      toast.error('Please enter a name');
      return;
    }

    setSaving(true);
    try {
      if (editingScheme) {
        await axios.put(`${API}/creditors/schemes/${editingScheme.id}`, {
          name: entityForm.name,
          description: entityForm.description,
          contact: entityForm.contact,
          is_active: entityForm.is_active,
          opening_balance: parseFloat(entityForm.opening_balance) || 0
        });
        toast.success('Investment scheme updated!');
      } else {
        await axios.post(`${API}/creditors/schemes`, {
          ...entityForm,
          store_id: selectedStoreId,
          opening_balance: parseFloat(entityForm.opening_balance) || 0
        });
        toast.success('Investment scheme added!');
      }
      setSchemeDialogOpen(false);
      setEditingScheme(null);
      setEntityForm({ name: '', description: '', contact: '', email: '', opening_balance: '', is_active: true });
      fetchAllData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save scheme');
    } finally {
      setSaving(false);
    }
  };

  const openEditCreditorDialog = (creditor) => {
    setEditingCreditor(creditor);
    setEntityForm({
      name: creditor.name || '',
      description: creditor.description || '',
      contact: creditor.contact || '',
      email: creditor.email || '',
      opening_balance: creditor.opening_balance ? creditor.opening_balance.toString() : '',
      is_active: creditor.is_active !== false
    });
    setCreditorDialogOpen(true);
  };

  const openEditSchemeDialog = (scheme) => {
    setEditingScheme(scheme);
    setEntityForm({
      name: scheme.name || '',
      description: scheme.description || '',
      contact: scheme.contact || '',
      email: scheme.email || '',
      opening_balance: scheme.opening_balance ? scheme.opening_balance.toString() : '',
      is_active: scheme.is_active !== false
    });
    setSchemeDialogOpen(true);
  };

  const openNewCreditorDialog = () => {
    setEditingCreditor(null);
    setEntityForm({ name: '', description: '', contact: '', email: '', opening_balance: '', is_active: true });
    setCreditorDialogOpen(true);
  };

  const openNewSchemeDialog = () => {
    setEditingScheme(null);
    setEntityForm({ name: '', description: '', contact: '', email: '', opening_balance: '', is_active: true });
    setSchemeDialogOpen(true);
  };

  const openDetailDialog = async (entity, type) => {
    setSelectedEntity({ ...entity, type });
    const filtered = transactions.filter(
      t => t.entity_id === entity.id && t.transaction_type === type
    );
    setEntityTransactions(filtered);
    setDetailDialogTab('transactions');
    setCreditorRequests([]);
    setDetailDialogOpen(true);
    
    // Fetch creditor requests if this is a creditor (not investment scheme)
    if (type === 'lending') {
      setLoadingRequests(true);
      try {
        const response = await axios.get(
          `${API}/creditor-requests/for-creditor/${entity.id}?store_id=${selectedStoreId}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        setCreditorRequests(response.data);
      } catch (error) {
        console.error('Failed to fetch creditor requests:', error);
      } finally {
        setLoadingRequests(false);
      }
    }
  };

  const handleUnreconcile = async () => {
    if (!unreconcileDialog.transaction) return;
    
    const reconciliationId = unreconcileDialog.transaction.reconciliation_id;
    if (!reconciliationId) {
      toast.error('No reconciliation ID found');
      return;
    }
    
    try {
      setUnreconciling(true);
      await axios.post(
        `${API}/reconciliation/unreconcile/${reconciliationId}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success('Transaction unreconciled successfully');
      setUnreconcileDialog({ open: false, transaction: null });
      
      // Refresh all data
      await fetchAllData();
      
      // Refresh the detail dialog data directly from API
      if (selectedEntity) {
        try {
          // Fetch fresh transactions for this entity
          const txnResponse = await axios.get(
            `${API}/creditors/transactions?store_id=${selectedStoreId}`,
            { headers: { Authorization: `Bearer ${token}` } }
          );
          const freshTransactions = txnResponse.data;
          const filtered = freshTransactions.filter(
            t => t.entity_id === selectedEntity.id && t.transaction_type === selectedEntity.type
          );
          setEntityTransactions(filtered);
          
          // Also refresh creditor requests
          if (selectedEntity.type === 'lending') {
            const reqResponse = await axios.get(
              `${API}/creditor-requests/for-creditor/${selectedEntity.id}?store_id=${selectedStoreId}`,
              { headers: { Authorization: `Bearer ${token}` } }
            );
            setCreditorRequests(reqResponse.data);
          }
        } catch (refreshError) {
          console.error('Failed to refresh dialog data:', refreshError);
        }
      }
    } catch (error) {
      console.error('Failed to unreconcile:', error);
      toast.error(error.response?.data?.detail || 'Failed to unreconcile');
    } finally {
      setUnreconciling(false);
    }
  };

  // TEMPORARY: Handle manual reconciliation for legacy transactions
  const handleManualReconcile = async () => {
    if (!manualReconcileDialog.transaction) return;
    
    try {
      setManualReconciling(true);
      await axios.post(
        `${API}/creditors/transactions/${manualReconcileDialog.transaction.id}/mark-reconciled`,
        { note: manualReconcileNote },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success('Transaction marked as reconciled');
      setManualReconcileDialog({ open: false, transaction: null });
      setManualReconcileNote('');
      
      // Refresh all data
      await fetchAllData();
      
      // Refresh the detail dialog data
      if (selectedEntity) {
        try {
          const txnResponse = await axios.get(
            `${API}/creditors/transactions?store_id=${selectedStoreId}`,
            { headers: { Authorization: `Bearer ${token}` } }
          );
          const freshTransactions = txnResponse.data;
          const filtered = freshTransactions.filter(
            t => t.entity_id === selectedEntity.id && t.transaction_type === selectedEntity.type
          );
          setEntityTransactions(filtered);
        } catch (refreshError) {
          console.error('Failed to refresh dialog data:', refreshError);
        }
      }
    } catch (error) {
      console.error('Failed to mark as reconciled:', error);
      toast.error(error.response?.data?.detail || 'Failed to mark as reconciled');
    } finally {
      setManualReconciling(false);
    }
  };

  const getEntitiesForType = () => {
    if (transactionForm.transaction_type === 'lending') {
      return creditors.filter(c => c.is_active);
    } else if (transactionForm.transaction_type === 'investment') {
      return schemes.filter(s => s.is_active);
    }
    return [];
  };

  return (
    <div className="space-y-6" data-testid="creditors-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight" style={{ fontFamily: 'Barlow Condensed' }}>
            Creditors & Investments
          </h1>
          <p className="text-muted-foreground mt-1">
            Track lending and investment activities
          </p>
        </div>

        <div className="flex items-center gap-3">
          <Select value={selectedStoreId} onValueChange={setSelectedStoreId}>
            <SelectTrigger className="w-[200px]" data-testid="store-select">
              <SelectValue placeholder="Select store" />
            </SelectTrigger>
            <SelectContent>
              {stores.map(store => (
                <SelectItem key={store.id} value={store.id}>{store.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>

          <Button onClick={() => openTransactionDialog()} data-testid="add-transaction-btn">
            <Plus className="h-4 w-4 mr-2" />
            New Entry
          </Button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className={`grid grid-cols-1 ${isStaff ? 'md:grid-cols-1' : 'md:grid-cols-3'} gap-4`}>
        <Card 
          className="card-hover cursor-pointer border-amber-500/20"
          onClick={() => summary?.creditor_details?.length > 0 && setActiveTab('lending')}
          data-testid="total-lent-card"
        >
          <CardContent className="p-5">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Total Lent (Outstanding)</p>
                <p className="text-2xl font-bold font-mono text-amber-400">
                  £{(summary?.total_lent_outstanding || 0).toFixed(2)}
                </p>
                <p className="text-xs text-muted-foreground mt-1">
                  {summary?.creditor_details?.length || 0} creditor(s)
                </p>
              </div>
              <div className="p-3 rounded-xl bg-amber-500/20">
                <Users className="h-6 w-6 text-amber-400" />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Hide Investments card for staff */}
        {!isStaff && (
          <Card 
            className="card-hover cursor-pointer border-purple-500/20"
            onClick={() => summary?.investment_details?.length > 0 && setActiveTab('investments')}
            data-testid="total-invested-card"
          >
            <CardContent className="p-5">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Total Invested (Outstanding)</p>
                  <p className="text-2xl font-bold font-mono text-purple-400">
                    £{(summary?.total_invested_outstanding || 0).toFixed(2)}
                  </p>
                  <p className="text-xs text-muted-foreground mt-1">
                    {summary?.investment_details?.length || 0} scheme(s)
                  </p>
                </div>
                <div className="p-3 rounded-xl bg-purple-500/20">
                  <Landmark className="h-6 w-6 text-purple-400" />
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Hide Cash Holding card for staff */}
        {!isStaff && (
          <Card className="card-hover border-green-500/20">
            <CardContent className="p-5">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Cash Holding</p>
                  <p className="text-2xl font-bold font-mono text-green-400">
                    £{(summary?.cash_holding || 0).toFixed(2)}
                  </p>
                  <p className="text-xs text-muted-foreground mt-1">Available for lending</p>
                </div>
                <div className="p-3 rounded-xl bg-green-500/20">
                  <Banknote className="h-6 w-6 text-green-400" />
                </div>
              </div>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Main Content */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <div className="flex items-center justify-between">
          <TabsList>
            <TabsTrigger value="overview" data-testid="tab-overview">Overview</TabsTrigger>
            <TabsTrigger value="lending" data-testid="tab-lending">Creditors</TabsTrigger>
            {!isStaff && (
              <TabsTrigger value="investments" data-testid="tab-investments">Investments</TabsTrigger>
            )}
          </TabsList>
          
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={openCreditorDialog} data-testid="add-creditor-btn">
              <Plus className="h-4 w-4 mr-1" />
              Add Creditor
            </Button>
            {!isStaff && (
              <Button variant="outline" size="sm" onClick={openSchemeDialog} data-testid="add-scheme-btn">
                <Plus className="h-4 w-4 mr-1" />
                Add Scheme
              </Button>
            )}
          </div>
        </div>

        {/* Overview Tab - Recent Transactions */}
        <TabsContent value="overview" className="mt-4">
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Recent Transactions</CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              {loading ? (
                <div className="flex items-center justify-center h-64">
                  <div className="spinner" />
                </div>
              ) : transactions.length === 0 ? (
                <div className="flex flex-col items-center justify-center h-64 text-muted-foreground">
                  <TrendingUp className="h-12 w-12 mb-4 opacity-50" />
                  <p>No transactions recorded</p>
                  <Button variant="outline" className="mt-4" onClick={() => openTransactionDialog()}>
                    <Plus className="h-4 w-4 mr-2" />
                    Record First Entry
                  </Button>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-border bg-muted/30">
                        <th className="text-left p-4 font-medium text-muted-foreground">ID</th>
                        <th className="text-left p-4 font-medium text-muted-foreground">Date</th>
                        <th className="text-left p-4 font-medium text-muted-foreground">Type</th>
                        <th className="text-left p-4 font-medium text-muted-foreground">Creditor/Scheme</th>
                        <th className="text-left p-4 font-medium text-muted-foreground">Description</th>
                        <th className="text-right p-4 font-medium text-muted-foreground">Amount</th>
                        <th className="text-right p-4 font-medium text-muted-foreground">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(() => {
                        // For staff, find their most recent transaction
                        const staffLastTransaction = isStaff 
                          ? transactions.find(t => t.created_by === user?.user_id)
                          : null;
                        
                        return transactions.slice(0, 20).map((transaction) => {
                          // Staff can only edit their most recent transaction
                          const canEdit = isStaff 
                            ? (staffLastTransaction && transaction.id === staffLastTransaction.id)
                            : true; // Admin/Manager can edit all
                          
                          return (
                            <tr 
                              key={transaction.id} 
                              className="border-b border-border/50 table-row-hover cursor-pointer"
                              onClick={() => canEdit && openTransactionDialog(transaction)}
                              data-testid={`transaction-row-${transaction.id}`}
                            >
                              <td className="p-4 font-mono text-xs text-primary">
                                {transaction.transaction_id || '-'}
                              </td>
                          <td className="p-4">
                            {new Date(transaction.date).toLocaleDateString('en-GB')}
                          </td>
                          <td className="p-4">
                            <span className={`px-2 py-1 rounded-full text-xs ${
                              transaction.transaction_type === 'lending' 
                                ? 'bg-amber-500/20 text-amber-400' 
                                : 'bg-purple-500/20 text-purple-400'
                            }`}>
                              {transaction.transaction_type === 'lending' ? 'Lending' : 'Investment'}
                            </span>
                          </td>
                          <td className="p-4 font-medium">{transaction.entity_name}</td>
                          <td className="p-4 text-muted-foreground max-w-xs truncate">
                            {transaction.description || '-'}
                          </td>
                          <td className={`p-4 text-right font-mono font-semibold ${
                            transaction.direction === 'out' ? 'text-red-400' : 'text-green-400'
                          }`}>
                            {transaction.direction === 'out' ? '-' : '+'}£{transaction.amount.toFixed(2)}
                          </td>
                          <td className="p-4 text-right">
                            {canEdit ? (
                              <Button
                                variant="ghost"
                                size="icon"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  openTransactionDialog(transaction);
                                }}
                                data-testid={`edit-transaction-${transaction.id}`}
                              >
                                <Pencil className="h-4 w-4" />
                              </Button>
                            ) : (
                              <span className="text-xs text-muted-foreground">-</span>
                            )}
                          </td>
                        </tr>
                          );
                        });
                      })()}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Creditors Tab */}
        <TabsContent value="lending" className="mt-4">
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">All Creditors</CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              {creditors.length === 0 ? (
                <div className="flex flex-col items-center justify-center h-64 text-muted-foreground">
                  <Users className="h-12 w-12 mb-4 opacity-50" />
                  <p>No creditors found</p>
                  <Button variant="outline" className="mt-4" onClick={openCreditorDialog}>
                    <Plus className="h-4 w-4 mr-2" />
                    Add Creditor
                  </Button>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-border bg-muted/30">
                        <th className="text-left p-4 font-medium text-muted-foreground">Creditor</th>
                        <th className="text-left p-4 font-medium text-muted-foreground">Contact</th>
                        <th className="text-center p-4 font-medium text-muted-foreground">Status</th>
                        <th className="text-right p-4 font-medium text-muted-foreground">Outstanding</th>
                        <th className="text-right p-4 font-medium text-muted-foreground">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {creditors.map((creditor) => {
                        // Find summary data if available
                        const summaryData = summary?.creditor_details?.find(c => c.id === creditor.id);
                        const outstanding = summaryData?.outstanding || 0;
                        
                        return (
                          <tr 
                            key={creditor.id} 
                            className={`border-b border-border/50 table-row-hover cursor-pointer ${!creditor.is_active ? 'opacity-60' : ''}`}
                            onClick={() => summaryData && openDetailDialog(summaryData, 'lending')}
                            data-testid={`creditor-row-${creditor.id}`}
                          >
                            <td className="p-4">
                              <div className="font-medium">{creditor.name}</div>
                              {creditor.email && (
                                <div className="text-xs text-muted-foreground">{creditor.email}</div>
                              )}
                              {creditor.description && (
                                <div className="text-xs text-muted-foreground mt-1">{creditor.description}</div>
                              )}
                            </td>
                            <td className="p-4 text-muted-foreground">
                              {creditor.contact || '-'}
                            </td>
                            <td className="p-4 text-center">
                              <span className={`px-2 py-1 rounded-full text-xs ${
                                creditor.is_active 
                                  ? 'bg-green-500/20 text-green-400' 
                                  : 'bg-red-500/20 text-red-400'
                              }`}>
                                {creditor.is_active ? 'Active' : 'Inactive'}
                              </span>
                            </td>
                            <td className={`p-4 text-right font-mono font-semibold ${
                              outstanding > 0 ? 'text-amber-400' : 'text-muted-foreground'
                            }`}>
                              £{outstanding.toFixed(2)}
                            </td>
                            <td className="p-4 text-right">
                              <Button
                                variant="ghost"
                                size="icon"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  openEditCreditorDialog(creditor);
                                }}
                                title="Edit Creditor"
                                data-testid={`edit-creditor-${creditor.id}`}
                              >
                                <Pencil className="h-4 w-4" />
                              </Button>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Investments Tab */}
        <TabsContent value="investments" className="mt-4">
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">All Investment Schemes</CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              {schemes.length === 0 ? (
                <div className="flex flex-col items-center justify-center h-64 text-muted-foreground">
                  <Landmark className="h-12 w-12 mb-4 opacity-50" />
                  <p>No investment schemes found</p>
                  <Button variant="outline" className="mt-4" onClick={openSchemeDialog}>
                    <Plus className="h-4 w-4 mr-2" />
                    Add Scheme
                  </Button>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-border bg-muted/30">
                        <th className="text-left p-4 font-medium text-muted-foreground">Scheme</th>
                        <th className="text-left p-4 font-medium text-muted-foreground">Contact</th>
                        <th className="text-center p-4 font-medium text-muted-foreground">Status</th>
                        <th className="text-right p-4 font-medium text-muted-foreground">Outstanding</th>
                        <th className="text-right p-4 font-medium text-muted-foreground">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {schemes.map((scheme) => {
                        // Find summary data if available
                        const summaryData = summary?.investment_details?.find(i => i.id === scheme.id);
                        const outstanding = summaryData?.outstanding || 0;
                        
                        return (
                          <tr 
                            key={scheme.id} 
                            className={`border-b border-border/50 table-row-hover cursor-pointer ${!scheme.is_active ? 'opacity-60' : ''}`}
                            onClick={() => summaryData && openDetailDialog(summaryData, 'investment')}
                            data-testid={`scheme-row-${scheme.id}`}
                          >
                            <td className="p-4">
                              <div className="font-medium">{scheme.name}</div>
                              {scheme.description && (
                                <div className="text-xs text-muted-foreground mt-1">{scheme.description}</div>
                              )}
                            </td>
                            <td className="p-4 text-muted-foreground">
                              {scheme.contact || '-'}
                            </td>
                            <td className="p-4 text-center">
                              <span className={`px-2 py-1 rounded-full text-xs ${
                                scheme.is_active 
                                  ? 'bg-green-500/20 text-green-400' 
                                  : 'bg-red-500/20 text-red-400'
                              }`}>
                                {scheme.is_active ? 'Active' : 'Inactive'}
                              </span>
                            </td>
                            <td className={`p-4 text-right font-mono font-semibold ${
                              outstanding > 0 ? 'text-purple-400' : 'text-muted-foreground'
                            }`}>
                              £{outstanding.toFixed(2)}
                            </td>
                            <td className="p-4 text-right">
                              <Button
                                variant="ghost"
                                size="icon"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  openEditSchemeDialog(scheme);
                                }}
                                title="Edit Scheme"
                                data-testid={`edit-scheme-${scheme.id}`}
                              >
                                <Pencil className="h-4 w-4" />
                              </Button>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Transaction Dialog */}
      <Dialog open={transactionDialogOpen} onOpenChange={setTransactionDialogOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] flex flex-col">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Barlow Condensed' }}>
              {editingTransaction ? 'Edit Transaction' : 'New Lending/Investment Entry'}
            </DialogTitle>
          </DialogHeader>

          <div className="flex-1 overflow-y-auto space-y-3 pr-2">
            {/* Row 1: Date & Transfer Type */}
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label className="text-xs">Date *</Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button variant="outline" className="w-full justify-start h-9 text-sm">
                      <CalendarIcon className="mr-2 h-3.5 w-3.5" />
                      {format(transactionForm.date, 'PPP')}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0">
                    <Calendar
                      mode="single"
                      selected={transactionForm.date}
                      onSelect={(date) => date && setTransactionForm({ ...transactionForm, date })}
                    />
                  </PopoverContent>
                </Popover>
              </div>

              <div className="space-y-1">
                <Label className="text-xs">Transfer Type *</Label>
                {isStaff ? (
                  // Staff can only do Lending - auto-select and show disabled
                  <div className="h-9 px-3 flex items-center bg-muted rounded-md border text-sm">
                    Lending
                  </div>
                ) : (
                  <Select 
                    value={transactionForm.transaction_type} 
                    onValueChange={(value) => {
                      setTransactionForm({ 
                        ...transactionForm, 
                        transaction_type: value,
                        entity_id: '',
                        entity_name: ''
                      });
                      setSelectedEntityBalance(null);
                    }}
                  >
                    <SelectTrigger data-testid="transaction-type-select" className="h-9">
                      <SelectValue placeholder="Select type" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="lending">Lending</SelectItem>
                      <SelectItem value="investment">Investment</SelectItem>
                    </SelectContent>
                  </Select>
                )}
              </div>
            </div>

            {/* Row 2: Creditor/Scheme */}
            <div className="space-y-1">
              <Label className="text-xs">{transactionForm.transaction_type === 'lending' ? 'Creditor' : 'Scheme'} *</Label>
              <Select 
                value={transactionForm.entity_id}
                onValueChange={(value) => handleEntitySelect(value, transactionForm.transaction_type)}
                disabled={!transactionForm.transaction_type}
              >
                <SelectTrigger data-testid="entity-select" className="h-9">
                  <SelectValue placeholder={`Select ${transactionForm.transaction_type === 'lending' ? 'creditor' : 'scheme'}`} />
                </SelectTrigger>
                <SelectContent>
                  {getEntitiesForType().map(entity => (
                    <SelectItem key={entity.id} value={entity.id}>{entity.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Compact Balance Display */}
            {selectedEntityBalance && (
              <div className={`px-3 py-2 rounded-lg border text-sm ${
                selectedEntityBalance.type === 'lending' 
                  ? 'bg-blue-500/10 border-blue-500/30' 
                  : 'bg-purple-500/10 border-purple-500/30'
              }`}>
                <div className="flex items-center justify-between">
                  <span className="text-muted-foreground text-xs">{selectedEntityBalance.name}</span>
                  <span className={`font-bold font-mono ${
                    selectedEntityBalance.outstanding > 0 
                      ? selectedEntityBalance.type === 'lending' ? 'text-blue-400' : 'text-purple-400'
                      : 'text-green-400'
                  }`}>
                    £{selectedEntityBalance.outstanding.toFixed(2)}
                  </span>
                </div>
                <div className="flex gap-4 text-xs mt-1">
                  <span className="text-muted-foreground">
                    {selectedEntityBalance.type === 'lending' ? 'Lent' : 'Invested'}: 
                    <span className="ml-1 font-mono text-red-400">
                      £{(selectedEntityBalance.total_lent || selectedEntityBalance.total_invested || 0).toFixed(2)}
                    </span>
                  </span>
                  <span className="text-muted-foreground">
                    Returned: <span className="ml-1 font-mono text-green-400">£{selectedEntityBalance.total_returned.toFixed(2)}</span>
                  </span>
                </div>
              </div>
            )}

            {/* Row 3: Direction & Pay From/Into */}
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label className="text-xs">Direction *</Label>
                <Select 
                  value={transactionForm.direction}
                  onValueChange={(value) => setTransactionForm({ ...transactionForm, direction: value })}
                >
                  <SelectTrigger data-testid="direction-select" className="h-9">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="out">
                      <div className="flex items-center gap-2">
                        <ArrowUpRight className="h-3.5 w-3.5 text-red-400" />
                        {transactionForm.transaction_type === 'lending' ? 'Lend' : 'Invest'}
                      </div>
                    </SelectItem>
                    <SelectItem value="in">
                      <div className="flex items-center gap-2">
                        <ArrowDownRight className="h-3.5 w-3.5 text-green-400" />
                        {transactionForm.transaction_type === 'lending' ? 'Repayment' : 'Return'}
                      </div>
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-1">
                <Label className="text-xs">{transactionForm.direction === 'in' ? 'Pay Into' : 'Pay From'} *</Label>
                {isStaff ? (
                  // Staff can only use Cash Holding - auto-select and show disabled
                  <div className="h-9 px-3 flex items-center gap-2 bg-muted rounded-md border text-sm">
                    <Banknote className="h-3.5 w-3.5 text-green-400" />
                    Cash Holding
                  </div>
                ) : (
                  <Select 
                    value={transactionForm.source}
                    onValueChange={(value) => setTransactionForm({ ...transactionForm, source: value })}
                  >
                    <SelectTrigger data-testid="source-select" className="h-9">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="cash_holding">
                        <div className="flex items-center gap-2">
                          <Banknote className="h-3.5 w-3.5 text-green-400" />
                          Cash Holding
                        </div>
                      </SelectItem>
                      <SelectItem value="safe">
                        <div className="flex items-center gap-2">
                          <Shield className="h-3.5 w-3.5 text-amber-400" />
                          Safe
                        </div>
                      </SelectItem>
                    </SelectContent>
                  </Select>
                )}
              </div>
            </div>

            {/* Row 4: Amount & Description */}
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label className="text-xs">Amount (£) *</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={transactionForm.amount}
                  onChange={(e) => setTransactionForm({ ...transactionForm, amount: e.target.value })}
                  className="font-mono h-9"
                  placeholder="0.00"
                  data-testid="transaction-amount"
                />
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Description *</Label>
                <Input
                  value={transactionForm.description}
                  onChange={(e) => setTransactionForm({ ...transactionForm, description: e.target.value })}
                  placeholder="Enter description..."
                  className="h-9"
                  data-testid="transaction-description"
                />
              </div>
            </div>

            {/* Compact Balance Info - hide for staff */}
            {transactionForm.direction === 'out' && !isStaff && (
              <div className="flex items-center justify-between px-3 py-2 bg-muted/50 rounded-lg text-xs">
                <div className="flex gap-4">
                  {balances.cash && (
                    <span className={transactionForm.source === 'cash_holding' ? 'text-green-400' : 'text-muted-foreground'}>
                      Cash: <span className="font-mono">£{balances.cash.current_balance.toFixed(2)}</span>
                    </span>
                  )}
                  {balances.safe && (
                    <span className={transactionForm.source === 'safe' ? 'text-amber-400' : 'text-muted-foreground'}>
                      Safe: <span className="font-mono">£{balances.safe.current_balance.toFixed(2)}</span>
                    </span>
                  )}
                </div>
                {transactionForm.amount && parseFloat(transactionForm.amount) > 0 && (
                  <span className={`font-mono ${
                    (transactionForm.source === 'safe' 
                      ? (balances.safe?.current_balance || 0) - parseFloat(transactionForm.amount)
                      : (balances.cash?.current_balance || 0) - parseFloat(transactionForm.amount)) < 0 
                    ? 'text-red-400' : 'text-muted-foreground'
                  }`}>
                    After: £{(
                      (transactionForm.source === 'safe' 
                        ? (balances.safe?.current_balance || 0) 
                        : (balances.cash?.current_balance || 0)) - parseFloat(transactionForm.amount)
                    ).toFixed(2)}
                  </span>
                )}
              </div>
            )}
          </div>

          <DialogFooter className="pt-4 border-t border-border mt-4">
            <Button variant="outline" onClick={() => setTransactionDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleTransactionSubmit} disabled={saving} data-testid="save-transaction-btn">
              {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
              {editingTransaction ? 'Update' : 'Save Entry'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add/Edit Creditor Dialog */}
      <Dialog open={creditorDialogOpen} onOpenChange={(open) => {
        setCreditorDialogOpen(open);
        if (!open) {
          setEditingCreditor(null);
          setEntityForm({ name: '', description: '', contact: '', email: '', opening_balance: '' });
        }
      }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Barlow Condensed' }}>
              {editingCreditor ? 'Edit Creditor' : 'Add Creditor'}
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Name *</Label>
              <Input
                value={entityForm.name}
                onChange={(e) => setEntityForm({ ...entityForm, name: e.target.value })}
                placeholder="Creditor name"
                data-testid="creditor-name"
              />
            </div>
            <div className="space-y-2">
              <Label>Opening Balance (£)</Label>
              <Input
                type="number"
                step="0.01"
                value={entityForm.opening_balance}
                onChange={(e) => setEntityForm({ ...entityForm, opening_balance: e.target.value })}
                placeholder="0.00"
                className="font-mono"
                data-testid="creditor-opening-balance"
              />
              <p className="text-xs text-muted-foreground">
                {editingCreditor 
                  ? 'Update the opening balance amount (will create/update opening balance transaction)'
                  : 'Amount already owed by this creditor'}
              </p>
            </div>
            <div className="space-y-2">
              <Label>Email</Label>
              <Input
                type="email"
                value={entityForm.email}
                onChange={(e) => setEntityForm({ ...entityForm, email: e.target.value })}
                placeholder="creditor@email.com"
                data-testid="creditor-email"
              />
            </div>
            <div className="space-y-2">
              <Label>Phone/Contact</Label>
              <Input
                value={entityForm.contact}
                onChange={(e) => setEntityForm({ ...entityForm, contact: e.target.value })}
                placeholder="Phone number"
                data-testid="creditor-contact"
              />
            </div>
            <div className="space-y-2">
              <Label>Description</Label>
              <Textarea
                value={entityForm.description}
                onChange={(e) => setEntityForm({ ...entityForm, description: e.target.value })}
                placeholder="Optional notes"
                rows={2}
              />
            </div>
            {editingCreditor && (
              <div className="flex items-center gap-3 pt-2">
                <Checkbox
                  id="creditor-active"
                  checked={entityForm.is_active}
                  onCheckedChange={(checked) => setEntityForm({ ...entityForm, is_active: checked })}
                  data-testid="creditor-active-checkbox"
                />
                <Label htmlFor="creditor-active" className="cursor-pointer">
                  Active (can be selected for new transactions)
                </Label>
              </div>
            )}
          </div>

          <DialogFooter className="pt-4">
            <Button variant="outline" onClick={() => setCreditorDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleCreditorSubmit} disabled={saving} data-testid="save-creditor-btn">
              {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
              {editingCreditor ? 'Save Changes' : 'Add Creditor'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add/Edit Scheme Dialog */}
      <Dialog open={schemeDialogOpen} onOpenChange={(open) => {
        setSchemeDialogOpen(open);
        if (!open) {
          setEditingScheme(null);
          setEntityForm({ name: '', description: '', contact: '', email: '', opening_balance: '' });
        }
      }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Barlow Condensed' }}>
              {editingScheme ? 'Edit Investment Scheme' : 'Add Investment Scheme'}
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Name *</Label>
              <Input
                value={entityForm.name}
                onChange={(e) => setEntityForm({ ...entityForm, name: e.target.value })}
                placeholder="Scheme name"
                data-testid="scheme-name"
              />
            </div>
            <div className="space-y-2">
              <Label>Opening Balance (£)</Label>
              <Input
                type="number"
                step="0.01"
                value={entityForm.opening_balance}
                onChange={(e) => setEntityForm({ ...entityForm, opening_balance: e.target.value })}
                placeholder="0.00"
                className="font-mono"
                data-testid="scheme-opening-balance"
              />
              <p className="text-xs text-muted-foreground">
                {editingScheme 
                  ? 'Update the opening balance amount (will create/update opening balance transaction)'
                  : 'Amount already invested in this scheme'}
              </p>
            </div>
            <div className="space-y-2">
              <Label>Contact Person</Label>
              <Input
                value={entityForm.contact}
                onChange={(e) => setEntityForm({ ...entityForm, contact: e.target.value })}
                placeholder="Contact name or phone"
                data-testid="scheme-contact"
              />
            </div>
            <div className="space-y-2">
              <Label>Description</Label>
              <Textarea
                value={entityForm.description}
                onChange={(e) => setEntityForm({ ...entityForm, description: e.target.value })}
                placeholder="Optional notes"
                rows={2}
              />
            </div>
            {editingScheme && (
              <div className="flex items-center gap-3 pt-2">
                <Checkbox
                  id="scheme-active"
                  checked={entityForm.is_active}
                  onCheckedChange={(checked) => setEntityForm({ ...entityForm, is_active: checked })}
                  data-testid="scheme-active-checkbox"
                />
                <Label htmlFor="scheme-active" className="cursor-pointer">
                  Active (can be selected for new transactions)
                </Label>
              </div>
            )}
          </div>

          <DialogFooter className="pt-4">
            <Button variant="outline" onClick={() => setSchemeDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleSchemeSubmit} disabled={saving} data-testid="save-scheme-btn">
              {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
              {editingScheme ? 'Save Changes' : 'Add Scheme'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Detail Dialog - Transactions and Requests for specific creditor/scheme */}
      <Dialog open={detailDialogOpen} onOpenChange={setDetailDialogOpen}>
        <DialogContent className="max-w-3xl max-h-[80vh] flex flex-col overflow-hidden">
          <DialogHeader className="flex-shrink-0">
            <DialogTitle style={{ fontFamily: 'Barlow Condensed' }}>
              {selectedEntity?.name}
            </DialogTitle>
          </DialogHeader>

          {/* Tabs for Transactions and Requests */}
          {selectedEntity?.type === 'lending' ? (
            <Tabs value={detailDialogTab} onValueChange={setDetailDialogTab} className="flex-1 flex flex-col min-h-0 overflow-hidden">
              <TabsList className="grid w-full grid-cols-2 flex-shrink-0">
                <TabsTrigger value="transactions" className="flex items-center gap-2">
                  <Wallet className="h-4 w-4" />
                  Transactions
                </TabsTrigger>
                <TabsTrigger value="requests" className="flex items-center gap-2">
                  <FileText className="h-4 w-4" />
                  Requests
                  {creditorRequests.filter(r => r.reconciliation_status === 'unreconciled').length > 0 && (
                    <Badge className="bg-amber-500/20 text-amber-400 ml-1">
                      {creditorRequests.filter(r => r.reconciliation_status === 'unreconciled').length}
                    </Badge>
                  )}
                </TabsTrigger>
              </TabsList>

              {/* Transactions Tab */}
              <TabsContent value="transactions" className="flex-1 overflow-y-auto mt-4 min-h-0">
                {entityTransactions.length === 0 ? (
                  <div className="flex flex-col items-center justify-center h-40 text-muted-foreground">
                    <p>No transactions found</p>
                  </div>
                ) : (
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-border bg-muted/30">
                        <th className="text-left p-3 font-medium text-muted-foreground">Date</th>
                        <th className="text-left p-3 font-medium text-muted-foreground">Description</th>
                        <th className="text-right p-3 font-medium text-muted-foreground">Amount</th>
                        <th className="text-left p-3 font-medium text-muted-foreground">Status</th>
                        <th className="text-left p-3 font-medium text-muted-foreground">By</th>
                        <th className="text-right p-3 font-medium text-muted-foreground">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {entityTransactions.map((t) => (
                        <tr 
                          key={t.id} 
                          className="border-b border-border/50 table-row-hover"
                        >
                          <td 
                            className="p-3 cursor-pointer"
                            onClick={() => {
                              setDetailDialogOpen(false);
                              openTransactionDialog(t);
                            }}
                          >
                            {new Date(t.date).toLocaleDateString('en-GB')}
                          </td>
                          <td 
                            className="p-3 text-muted-foreground cursor-pointer"
                            onClick={() => {
                              setDetailDialogOpen(false);
                              openTransactionDialog(t);
                            }}
                          >
                            {t.description || '-'}
                          </td>
                          <td className={`p-3 text-right font-mono font-semibold ${
                            t.direction === 'out' ? 'text-red-400' : 'text-green-400'
                          }`}>
                            {t.direction === 'out' ? '-' : '+'}£{t.amount.toFixed(2)}
                          </td>
                          <td className="p-3">
                            {t.is_reconciled ? (
                              <Badge className={t.is_manual_reconciliation ? "bg-blue-500/20 text-blue-400" : "bg-green-500/20 text-green-400"}>
                                <CheckCircle2 className="h-3 w-3 mr-1" />
                                {t.is_manual_reconciliation ? 'Reconciled (Manual)' : 'Reconciled'}
                              </Badge>
                            ) : (
                              <Badge className="bg-muted text-muted-foreground">Unreconciled</Badge>
                            )}
                          </td>
                          <td className="p-3 text-sm text-muted-foreground">{t.created_by_name || '-'}</td>
                          <td className="p-3 text-right">
                            {t.is_reconciled && t.reconciliation_id && !t.is_manual_reconciliation && (
                              <Button
                                variant="ghost"
                                size="sm"
                                className="text-amber-500 hover:text-amber-600 hover:bg-amber-500/10"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setUnreconcileDialog({ open: true, transaction: t });
                                }}
                                data-testid={`unreconcile-${t.id}`}
                              >
                                <RotateCcw className="h-4 w-4 mr-1" />
                                Unreconcile
                              </Button>
                            )}
                            {/* TEMPORARY: Mark Reconciled button for legacy transactions */}
                            {!t.is_reconciled && (
                              <Button
                                variant="ghost"
                                size="sm"
                                className="text-green-500 hover:text-green-600 hover:bg-green-500/10"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setManualReconcileDialog({ open: true, transaction: t });
                                }}
                                data-testid={`mark-reconciled-${t.id}`}
                              >
                                <CheckCircle2 className="h-4 w-4 mr-1" />
                                Mark Reconciled
                              </Button>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </TabsContent>

              {/* Requests Tab */}
              {/* Requests Tab */}
              <TabsContent value="requests" className="flex-1 overflow-y-auto mt-4 min-h-0">
                {loadingRequests ? (
                  <div className="flex items-center justify-center h-40">
                    <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                  </div>
                ) : creditorRequests.length === 0 ? (
                  <div className="flex flex-col items-center justify-center h-40 text-muted-foreground">
                    <FileText className="h-8 w-8 mb-2 opacity-50" />
                    <p>No requests found</p>
                    <p className="text-sm">Creditor users can submit credit and repayment requests</p>
                  </div>
                ) : (
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-border bg-muted/30">
                        <th className="text-left p-3 font-medium text-muted-foreground">Date</th>
                        <th className="text-left p-3 font-medium text-muted-foreground">Type</th>
                        <th className="text-left p-3 font-medium text-muted-foreground">Reference</th>
                        <th className="text-right p-3 font-medium text-muted-foreground">Amount</th>
                        <th className="text-left p-3 font-medium text-muted-foreground">Status</th>
                        <th className="text-left p-3 font-medium text-muted-foreground">Submitted By</th>
                      </tr>
                    </thead>
                    <tbody>
                      {creditorRequests.map((req) => (
                        <tr 
                          key={req.id} 
                          className="border-b border-border/50 hover:bg-muted/30"
                        >
                          <td className="p-3">{new Date(req.date).toLocaleDateString('en-GB')}</td>
                          <td className="p-3">
                            <Badge className={req.request_type === 'credit_request' ? 'bg-red-500/20 text-red-400' : 'bg-green-500/20 text-green-400'}>
                              {req.request_type === 'credit_request' ? (
                                <><TrendingUp className="h-3 w-3 mr-1" />Credit</>
                              ) : (
                                <><ArrowDownRight className="h-3 w-3 mr-1" />Repayment</>
                              )}
                            </Badge>
                          </td>
                          <td className="p-3 text-muted-foreground">{req.reference || '-'}</td>
                          <td className={`p-3 text-right font-mono font-semibold ${
                            req.request_type === 'credit_request' ? 'text-red-400' : 'text-green-400'
                          }`}>
                            £{(req.amount || 0).toFixed(2)}
                          </td>
                          <td className="p-3">
                            <Badge className={req.reconciliation_status === 'reconciled' ? 'bg-green-500/20 text-green-400' : 'bg-amber-500/20 text-amber-400'}>
                              {req.reconciliation_status === 'reconciled' ? (
                                <><CheckCircle2 className="h-3 w-3 mr-1" />Reconciled</>
                              ) : (
                                <><Clock className="h-3 w-3 mr-1" />Pending</>
                              )}
                            </Badge>
                          </td>
                          <td className="p-3 text-sm text-muted-foreground">{req.submitted_by_name || '-'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </TabsContent>
            </Tabs>
          ) : (
            /* For investment schemes, just show transactions */
            <div className="flex-1 overflow-y-auto min-h-0">
              {entityTransactions.length === 0 ? (
                <div className="flex flex-col items-center justify-center h-40 text-muted-foreground">
                  <p>No transactions found</p>
                </div>
              ) : (
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-border bg-muted/30">
                      <th className="text-left p-3 font-medium text-muted-foreground">Date</th>
                      <th className="text-left p-3 font-medium text-muted-foreground">Description</th>
                      <th className="text-right p-3 font-medium text-muted-foreground">Amount</th>
                      <th className="text-left p-3 font-medium text-muted-foreground">By</th>
                    </tr>
                  </thead>
                  <tbody>
                    {entityTransactions.map((t) => (
                      <tr 
                        key={t.id} 
                        className="border-b border-border/50 table-row-hover cursor-pointer"
                        onClick={() => {
                          setDetailDialogOpen(false);
                          openTransactionDialog(t);
                        }}
                      >
                        <td className="p-3">{new Date(t.date).toLocaleDateString('en-GB')}</td>
                        <td className="p-3 text-muted-foreground">{t.description || '-'}</td>
                        <td className={`p-3 text-right font-mono font-semibold ${
                          t.direction === 'out' ? 'text-red-400' : 'text-green-400'
                        }`}>
                          {t.direction === 'out' ? '-' : '+'}£{t.amount.toFixed(2)}
                        </td>
                        <td className="p-3 text-sm text-muted-foreground">{t.created_by_name || '-'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          )}

          <DialogFooter className="pt-4 border-t border-border flex-shrink-0">
            <Button variant="outline" onClick={() => setDetailDialogOpen(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Unreconcile Confirmation Dialog */}
      <AlertDialog open={unreconcileDialog.open} onOpenChange={(open) => setUnreconcileDialog({ open, transaction: null })}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Unreconcile Transaction</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to unreconcile this transaction?
              {unreconcileDialog.transaction && (
                <div className="mt-3 p-3 bg-muted rounded-lg">
                  <p className="font-medium">{unreconcileDialog.transaction.description || 'Transaction'}</p>
                  <p className="text-sm">
                    Amount: <span className={unreconcileDialog.transaction.direction === 'out' ? 'text-red-400' : 'text-green-400'}>
                      {unreconcileDialog.transaction.direction === 'out' ? '-' : '+'}£{unreconcileDialog.transaction.amount?.toFixed(2)}
                    </span>
                  </p>
                  <p className="text-xs text-muted-foreground mt-1">
                    This will also unreconcile any linked credit/repayment requests.
                  </p>
                </div>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={unreconciling}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleUnreconcile}
              disabled={unreconciling}
              className="bg-amber-500 hover:bg-amber-600"
            >
              {unreconciling ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Unreconciling...
                </>
              ) : (
                <>
                  <RotateCcw className="h-4 w-4 mr-2" />
                  Unreconcile
                </>
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* TEMPORARY: Manual Reconcile Dialog for legacy transactions */}
      <AlertDialog 
        open={manualReconcileDialog.open} 
        onOpenChange={(open) => {
          setManualReconcileDialog({ open, transaction: null });
          setManualReconcileNote('');
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Mark as Reconciled (Legacy)</AlertDialogTitle>
            <AlertDialogDescription>
              Mark this legacy transaction as reconciled without matching it to a credit request.
              {manualReconcileDialog.transaction && (
                <div className="mt-3 p-3 bg-muted rounded-lg">
                  <p className="font-medium">{manualReconcileDialog.transaction.description || 'Transaction'}</p>
                  <p className="text-sm">
                    Amount: <span className={manualReconcileDialog.transaction.direction === 'out' ? 'text-red-400' : 'text-green-400'}>
                      {manualReconcileDialog.transaction.direction === 'out' ? '-' : '+'}£{manualReconcileDialog.transaction.amount?.toFixed(2)}
                    </span>
                  </p>
                  <p className="text-sm">
                    Date: {new Date(manualReconcileDialog.transaction.date).toLocaleDateString('en-GB')}
                  </p>
                </div>
              )}
              <div className="mt-4">
                <label className="text-sm font-medium">Note (optional)</label>
                <textarea
                  className="w-full mt-1 p-2 border rounded-md bg-background text-foreground text-sm"
                  rows={2}
                  placeholder="e.g., Settled before credit request system, Bank ref #123..."
                  value={manualReconcileNote}
                  onChange={(e) => setManualReconcileNote(e.target.value)}
                />
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={manualReconciling}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleManualReconcile}
              disabled={manualReconciling}
              className="bg-green-500 hover:bg-green-600"
            >
              {manualReconciling ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Marking...
                </>
              ) : (
                <>
                  <CheckCircle2 className="h-4 w-4 mr-2" />
                  Mark Reconciled
                </>
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default CreditorsPage;
